import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  X,
  Sparkles,
  Calendar,
  MapPin,
  Package,
  Building2,
} from 'lucide-react';
import { api } from '@/lib/api/client';
import { brandsApi } from '@/features/brands/api';
import { queryKeys } from '@/lib/api/queryKeys';
import type { XkEvent, XkStation, XkStock } from '@/lib/api/types';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';

type Kind = 'brand' | 'event' | 'station' | 'stock';

interface Hit {
  kind: Kind;
  id: number;
  label: string;
  sublabel?: string;
  path: string;
}

const MAX_PER_KIND = 5;
const MAX_TOTAL = 20;

const KIND_KEY: Record<Kind, string> = {
  brand: 'globalSearch.kind.brand',
  event: 'globalSearch.kind.event',
  station: 'globalSearch.kind.station',
  stock: 'globalSearch.kind.stock',
};

const KIND_ICON: Record<Kind, typeof Sparkles> = {
  brand: Building2,
  event: Calendar,
  station: MapPin,
  stock: Package,
};

interface GlobalSearchProps {
  className?: string;
}

export function GlobalSearch({ className }: GlobalSearchProps) {
  const navigate = useNavigate();
  const t = useT();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const brandsQ = useQuery({
    queryKey: queryKeys.brands.all,
    queryFn: () => brandsApi.list(),
  });
  const eventsQ = useQuery({
    queryKey: queryKeys.events.all,
    queryFn: () => api.list<XkEvent>('/xk-event'),
  });
  const stationsQ = useQuery({
    queryKey: queryKeys.stations.all,
    queryFn: () => api.list<XkStation>('/xk-station'),
  });
  const stockQ = useQuery({
    queryKey: queryKeys.stock.all,
    queryFn: () => api.list<XkStock>('/xk-stock'),
  });

  const trimmed = query.trim();
  const lower = trimmed.toLowerCase();

  const hits: Hit[] = useMemo(() => {
    if (!lower) return [];
    const brandHits: Hit[] = (brandsQ.data?.items ?? [])
      .filter((b) => b.name?.toLowerCase().includes(lower))
      .slice(0, MAX_PER_KIND)
      .map((b) => ({
        kind: 'brand',
        id: b.id,
        label: b.name,
        sublabel: t('globalSearch.subBrand'),
        path: `/brand/${b.id}`,
      }));

    const eventHits: Hit[] = (eventsQ.data?.items ?? [])
      .filter(
        (e) =>
          e.name?.toLowerCase().includes(lower) ||
          e.address?.toLowerCase().includes(lower) ||
          e.codeRef?.toLowerCase().includes(lower),
      )
      .slice(0, MAX_PER_KIND)
      .map((e) => ({
        kind: 'event',
        id: e.id,
        label: e.name,
        sublabel: e.address || e.codeRef || `${t('globalSearch.subEvent')} #${e.id}`,
        path: `/projects/${e.eventGroupId}/events/${e.id}`,
      }));

    const stationHits: Hit[] = (stationsQ.data?.items ?? [])
      .filter((s) => s.name?.toLowerCase().includes(lower))
      .slice(0, MAX_PER_KIND)
      .map((s) => ({
        kind: 'station',
        id: s.id,
        label: s.name,
        sublabel: `${t('globalSearch.subEvent')} #${s.eventId}`,
        path: `/stations/${s.id}`,
      }));

    const stockHits: Hit[] = (stockQ.data?.items ?? [])
      .filter(
        (s) =>
          s.name?.toLowerCase().includes(lower) ||
          s.translate?.toLowerCase().includes(lower),
      )
      .slice(0, MAX_PER_KIND)
      .map((s) => ({
        kind: 'stock',
        id: s.id,
        label: s.name,
        sublabel: s.classification || t('globalSearch.subStockItem'),
        path: `/stock`,
      }));

    return [...brandHits, ...eventHits, ...stationHits, ...stockHits].slice(
      0,
      MAX_TOTAL,
    );
  }, [lower, brandsQ.data, eventsQ.data, stationsQ.data, stockQ.data, t]);

  // Group hits by kind to render section headers.
  const groups = useMemo(() => {
    const buckets: Record<Kind, Hit[]> = {
      brand: [],
      event: [],
      station: [],
      stock: [],
    };
    for (const h of hits) buckets[h.kind].push(h);
    return (['brand', 'event', 'station', 'stock'] as const)
      .map((k) => ({ kind: k, items: buckets[k] }))
      .filter((g) => g.items.length > 0);
  }, [hits]);

  // Close on outside click + Esc.
  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    }
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const select = (hit: Hit) => {
    navigate(hit.path);
    setOpen(false);
    setQuery('');
  };

  const showPanel = open && (trimmed.length > 0 || hits.length === 0);

  return (
    <div ref={wrapperRef} className={cn('relative', className)}>
      {/* Input */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-xk-text-subtle)]" />
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={t('globalSearch.placeholder')}
          className="w-full rounded-md border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] py-1.5 pl-9 pr-9 text-[13px] text-[var(--color-xk-lavender)] placeholder:text-[var(--color-xk-text-subtle)] focus:border-[var(--color-accent-ink)] focus:outline-none"
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery('');
              inputRef.current?.focus();
            }}
            aria-label={t('globalSearch.clear')}
            className="absolute right-2 top-1/2 grid h-5 w-5 -translate-y-1/2 place-items-center rounded text-[var(--color-xk-text-muted)] hover:bg-[var(--color-xk-surface-2)] hover:text-[var(--color-xk-lavender)]"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Floating results panel */}
      {showPanel && (
        <div className="glass-panel absolute left-0 right-0 top-[calc(100%+8px)] z-50 max-h-[min(60vh,420px)] overflow-y-auto scrollbar-thin p-1.5">
          {hits.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-[var(--color-xk-text-muted)]">
              {t('globalSearch.noMatches')}{' '}
              <span className="font-semibold text-[var(--color-xk-lavender)]">
                {trimmed}
              </span>
            </div>
          ) : (
            groups.map((group, gi) => {
              const Icon = KIND_ICON[group.kind];
              return (
                <div key={group.kind} className={gi > 0 ? 'mt-2' : ''}>
                  <div
                    className="px-3 pb-1 pt-2 text-[10px] font-extrabold uppercase tracking-[0.12em] text-[var(--color-xk-text-subtle)]"
                    style={{ fontFamily: 'var(--font-display)' }}
                  >
                    {t(KIND_KEY[group.kind])}
                  </div>
                  <ul>
                    {group.items.map((h) => (
                      <li key={`${h.kind}-${h.id}`}>
                        <button
                          type="button"
                          onClick={() => select(h)}
                          className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-[var(--color-accent-10)]"
                        >
                          <span className="grid h-7 w-7 flex-none place-items-center rounded-md bg-[var(--color-xk-surface-2)] text-[var(--color-accent-ink)]">
                            <Icon className="h-3.5 w-3.5" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[13px] font-semibold text-[var(--color-xk-lavender)]">
                              {h.label}
                            </span>
                            {h.sublabel && (
                              <span className="block truncate text-[11px] text-[var(--color-xk-text-muted)]">
                                {h.sublabel}
                              </span>
                            )}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
